import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid2';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import * as urls from 'routes/config';
import {
  useCreatePublicOnboardingForm,
  useUpdatePublicOnboardingForm,
  useCreateCheckoutSession,
  useGetPublicOnboardingForm,
  useLoginOnboardingForm
} from 'apis/agency/businessOnboarding/hooks';
import type { PublicOnboardingFormRequest, OnboardingFormRequest } from 'apis/agency/businessOnboarding/types';
import { useGetOrganizations } from 'apis/agency/organizations/hook';
import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import { transformOpeningPeriods } from '@/utils/bussinesshours';
import { normalizeWebsiteUrl } from '@/utils/helperFunctions';
import { REASONS } from './components/AIReceptionistReasonsStep';
import { BACKEND_URL } from '@/env';

type StartAgentReadinessFormValues = {
  business_legal_name?: string;
  website_url?: string;
  address?: {
    street_address?: string;
    city?: string;
    state_province_region?: string;
    postal_zip_code?: string;
    country?: string;
    time_zone?: string;
  };
  firstName?: string;
  lastName?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  referralCode?: string;
  industry?: string;
  location_details?: {
    speciality?: string;
  };
  available_integration_ids?: string[];
  ai_receptionist_reasons?: string[];
  other_ai_receptionist_reason?: string;
  business_hours?: any;
  other_integration_name?: string;
  integration_configuration?: any;
  faq_question_set?: string;
  external_reference_name?: string;
  external_reference_id?: string;
  external_reference_source?: string;
};

function onboardingQueryDefaults(searchParams: URLSearchParams): Partial<StartAgentReadinessFormValues> {
  const out: Partial<StartAgentReadinessFormValues> = {};

  const first = searchParams.get('First-Name');
  if (first?.trim()) out.firstName = first.trim();

  const last = searchParams.get('Last-Name');
  if (last?.trim()) out.lastName = last.trim();

  const phone = searchParams.get('Phone-Number');
  if (phone?.trim()) out.primary_contact_phone = phone.trim();

  const email = searchParams.get('Email-Address');
  if (email?.trim()) out.primary_contact_email = email.trim();

  const company = searchParams.get('Company-Name');
  if (company?.trim()) out.business_legal_name = company.trim();

  const refName = searchParams.get('External-Reference-Name') || searchParams.get('external_reference_name');
  if (refName?.trim()) out.external_reference_name = refName.trim();

  const refId = searchParams.get('External-Reference-Id') || searchParams.get('external_reference_id');
  if (refId?.trim()) out.external_reference_id = refId.trim();

  const refSource = searchParams.get('External-Reference-Source') || searchParams.get('external_reference_source');
  if (refSource?.trim()) out.external_reference_source = refSource.trim();

  return out;
}

function buildOnboardingObject(
  values: StartAgentReadinessFormValues,
  stage: string | null = null,
  step: number | null = null,
  isUpdate: boolean = false,
  status?: any,
  currentOnboardingConfig?: Record<string, any>
): OnboardingFormRequest['onboarding_form'] {
  const primary_contact_name = [values.firstName, values.lastName].filter(Boolean).join(' ').trim();
  const referrer = Cookies.get('onboarding_referrer') || '';
  const hasRefParam = Boolean(referrer.trim());
  return {
    onboarding_config: {
      ...(currentOnboardingConfig || {}),
      stage: stage ? String(stage) : null,
      step: step ? String(step) : null
    },
    ...(status ? { status } : isUpdate ? {} : { status: 'draft' }),
    business_legal_name: values.business_legal_name,
    website_url: normalizeWebsiteUrl(values.website_url),
    industry: values.industry,
    primary_contact_name: primary_contact_name || undefined,
    primary_contact_email: values.primary_contact_email,
    primary_contact_phone: values.primary_contact_phone,
    external_reference_name: values.external_reference_name || undefined,
    external_reference_id: values.external_reference_id || undefined,
    external_reference_source: values.external_reference_source || undefined,
    address: {
      street_address: values.address?.street_address || '',
      city: values.address?.city || '',
      state_province_region: values.address?.state_province_region || '',
      postal_zip_code: values.address?.postal_zip_code || '',
      country: values.address?.country || '',
      time_zone: values.address?.time_zone || ''
    },
    current_business_solution: {
      telephony_voip_provider: values.external_reference_source || 'Other'
    },
    location_details: {},
    faq_question_set: values.faq_question_set || undefined,
    available_integration_ids:
      values.available_integration_ids?.[0] === ' ' || values.available_integration_ids?.[0] === 'other'
        ? []
        : values.available_integration_ids,
    available_integration_name:
      values.available_integration_ids?.[0] === ' ' || values.available_integration_ids?.[0] === 'other'
        ? values.available_integration_ids?.[0] === ' '
          ? 'No Integration'
          : 'other'
        : undefined,
    ai_receptionist_reasons: values.ai_receptionist_reasons?.map((r) =>
      r === 'Other' && values.other_ai_receptionist_reason ? values.other_ai_receptionist_reason : r
    ),
    ...(isUpdate ? {} : { source: hasRefParam ? 'referral' : 'organic' }),
    business_hours: transformOpeningPeriods(values.business_hours),
    operator_details: [
      {
        working_hours: transformOpeningPeriods(values.business_hours)
      }
    ],
    other_integration_name: values.other_integration_name
  };
}

function buildAgencyOnboardingPayload(
  values: StartAgentReadinessFormValues,
  stage: string | null = null,
  step: number | null = null
): PublicOnboardingFormRequest {
  return {
    create_onboarding_form: buildOnboardingObject(values, stage, step, false, undefined),
    update_message: ''
  };
}

const COOKIE_DATA_KEY = 'onboarding_data';
const COOKIE_CUSTOMER_ID_KEY = 'onboarding_customer_id';
const COOKIE_REFERRER_KEY = 'onboarding_referrer';
const COOKIE_PROGRAM_ID_KEY = 'onboarding_program_id';
const COOKIE_PARTNER_ID_KEY = 'onboarding_partner_id';
const COOKIE_TOLT_REFERRAL_KEY = 'tolt_referral';

const Onboarding = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryDefaults = useMemo(() => onboardingQueryDefaults(searchParams), [searchParams]);

  const { mutateAsync: submitAgencyOnboarding, isPending: isCreating } = useCreatePublicOnboardingForm();
  const { mutateAsync: updatePublicOnboarding, isPending: isUpdating } = useUpdatePublicOnboardingForm();
  const { mutateAsync: loginOnboardingForm } = useLoginOnboardingForm();
  const { mutateAsync: createCheckout, isPending: isSubmittingPayment } = useCreateCheckoutSession();

  const isSubmittingFinal = isCreating || isUpdating;

  const location = useLocation();
  const stateStep = location.state?.step;

  const initialData = JSON.parse(Cookies.get(COOKIE_DATA_KEY) || '{}');

  const { control, getValues, trigger, watch, setValue, reset } = useForm({
    defaultValues: {
      available_integration_ids: [],
      ai_receptionist_reasons: [],
      referralCode: '',
      ...initialData,
      ...queryDefaults, // URL params win over stale cookies
      industry: initialData.industry || 'healthcare',
      faq_question_set: initialData.faq_question_set || ''
    }
  });

  const hasExternalRef = useMemo(() => {
    return Boolean(
      queryDefaults.external_reference_name &&
        queryDefaults.external_reference_id &&
        queryDefaults.external_reference_source
    );
  }, [queryDefaults]);

  const [onboardingId, setOnboardingId] = useState(() => Cookies.get('onboarding_id') || '');

  const [formStep, setFormStep] = useState(() => {
    if (hasExternalRef) return 0;
    if (stateStep !== undefined) return Number(stateStep) - 1;
    if (typeof window !== 'undefined' && window.innerWidth < 900) {
      return -1;
    }
    return 0;
  });

  // Automatically advance from intro (-1) to first step (0) if switching to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 900 && formStep === -1) {
        setFormStep(0);
      }
    };
    window.addEventListener('resize', handleResize);
    // Also check immediately on mount or step change
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [formStep]);

  // Fetch onboarding data when formId is present and user is logged in
  const {
    data: onboardingDataResponse,
    refetch: refetchOnboardingData,
    isFetching: isFetchingForm
  } = useGetPublicOnboardingForm(onboardingId!, {
    enabled: !!(onboardingId && Cookies.get('auth_token'))
  });

  const formExternalReferenceSource = watch('external_reference_source');
  const externalReferenceSource = onboardingDataResponse?.external_reference_source || formExternalReferenceSource;

  // After form is created, lock email and phone (URL or typed — same behavior).
  // NetSapiens onboardings stay editable.
  const disabledFields = useMemo(() => {
    if (!onboardingId) return undefined;
    if (externalReferenceSource === 'netsapiens') return undefined;
    return {
      primary_contact_email: true,
      primary_contact_phone: true
    };
  }, [onboardingId, externalReferenceSource]);

  // Pre-fill form when data is fetched
  useEffect(() => {
    if (onboardingDataResponse && !isFetchingForm) {
      const data = onboardingDataResponse;
      const primaryContactName = data.primary_contact_name || '';
      const [firstName = '', ...lastNameParts] = primaryContactName.split(' ');
      const lastName = lastNameParts.join(' ');

      reset({
        ...getValues(),
        business_legal_name: data.business_legal_name || '',
        industry: data.industry || 'healthcare',
        website_url: data.website_url || '',
        primary_contact_email: data.primary_contact_email || '',
        primary_contact_phone: data.primary_contact_phone || '',
        firstName,
        lastName,
        address: {
          ...data.address,
          country: data.address?.country,
          time_zone: data.address?.time_zone
        },
        faq_question_set: data.faq_question_set || '',
        available_integration_ids: data.available_integration_ids?.length
          ? data.available_integration_ids
          : data.available_integration_name === 'other'
            ? ['other']
            : data.available_integration_name === 'No Integration'
              ? [' ']
              : [],
        ai_receptionist_reasons: (data.ai_receptionist_reasons || []).map((r: string) =>
          REASONS.includes(r) ? r : 'Other'
        ),
        other_ai_receptionist_reason:
          (data.ai_receptionist_reasons || []).find((r: string) => !REASONS.includes(r)) || '',
        business_hours: data.business_hours || {},
        other_integration_name: data.other_integration_name || ''
      });

      if (data.onboarding_config?.step && searchParams.get('success') !== 'true') {
        const apiStep = Number(data.onboarding_config.step) - 1;
        // Only sync if we aren't currently handling a successful payment (prevents flickering)
        if (apiStep >= 0 && apiStep <= 7) {
          setFormStep(apiStep);
        }
      }

      if (data.onboarding_config?.stage === '2') {
        navigate(`${urls.businessOnboarding.base}/stage-2`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingDataResponse, reset, getValues, isFetchingForm]);

  useEffect(() => {
    const customerId = searchParams.get('customer_id')?.trim();
    if (customerId) Cookies.set(COOKIE_CUSTOMER_ID_KEY, customerId, { expires: 30 });

    const referrer = searchParams.get('referrer')?.trim();
    if (referrer) Cookies.set(COOKIE_REFERRER_KEY, referrer, { expires: 30 });

    const programId = searchParams.get('program_id')?.trim();
    if (programId) Cookies.set(COOKIE_PROGRAM_ID_KEY, programId, { expires: 30 });

    const partnerId = searchParams.get('partner_id')?.trim();
    if (partnerId) Cookies.set(COOKIE_PARTNER_ID_KEY, partnerId, { expires: 30 });
  }, [searchParams]);

  // Handle success parameter from Stripe
  useEffect(() => {
    const isSuccess = searchParams.get('success') === 'true';

    if (onboardingId && isSuccess) {
      const values = getValues() as StartAgentReadinessFormValues;
      updatePublicOnboarding({
        formId: onboardingId,
        payload: {
          onboarding_form: buildOnboardingObject(
            values,
            '1',
            8,
            true,
            undefined,
            onboardingDataResponse?.onboarding_config
          ),
          update_message: ''
        }
      });
      setFormStep(7);

      // Clear so this effect cannot re-PATCH stage "1" after config updates / Lets Move
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('success');
      setSearchParams(nextParams, { replace: true });
    }
    // Omit onboardingDataResponse?.onboarding_config — it re-fires and overwrites stage "2"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, getValues, updatePublicOnboarding, onboardingId, setSearchParams]);

  // Auto-save form data to cookies whenever it changes
  const formValues = watch();
  useEffect(() => {
    Cookies.set(COOKIE_DATA_KEY, JSON.stringify(formValues), { expires: 30 }); // Store for 30 days
  }, [formValues]);

  const { data: organizationData } = useGetOrganizations();
  const billingEnabled = organizationData?.billing_enabled ?? true;

  const handleNext = async () => {
    const maxStep = billingEnabled ? 7 : 6;
    if (formStep === maxStep) {
      const values = getValues() as StartAgentReadinessFormValues;
      if (onboardingId) {
        try {
          await updatePublicOnboarding({
            formId: onboardingId,
            payload: {
              onboarding_form: buildOnboardingObject(
                values,
                '2',
                1,
                true,
                undefined,
                onboardingDataResponse?.onboarding_config
              ),
              update_message: ''
            }
          });
        } catch (error) {
          console.error('Error updating to stage 2:', error);
        }
      }
      navigate(`${urls.businessOnboarding.base}/${urls.businessOnboarding.stage2()}`);
      return;
    }
    if (formStep === -1) {
      setFormStep(0);
      return;
    }
    const isValid = await trigger();
    if (isValid) {
      try {
        const onboardToken = Cookies.get('onboard-token');
        const values = getValues() as StartAgentReadinessFormValues;

        if (formStep === 5 && organizationData?.billing_enabled === false) {
          // SKIP PAYMENT -> Direct to Stage 2
          const response = await updatePublicOnboarding({
            formId: onboardingId || onboardToken || '',
            payload: {
              onboarding_form: buildOnboardingObject(
                values,
                '2',
                1,
                true,
                'collecting_agent_info',
                onboardingDataResponse?.onboarding_config
              ),
              update_message: ''
            }
          });
          if (response) {
            navigate(`${urls.businessOnboarding.base}/stage-2`, { replace: true });
          }
          return;
        }

        if (formStep === 0 && !onboardingId) {
          const response = await submitAgencyOnboarding(buildAgencyOnboardingPayload(values, '1', formStep + 2));
          if (response) {
            const token = response.token || '';
            const id = response.id.toString();
            Cookies.set('onboarding_id', id, { expires: 30 });
            Cookies.set('onboard-token', token, { expires: 30 });
            setOnboardingId(id);
            // Call login API
            try {
              const loginResponse = await loginOnboardingForm({
                token: token,
                update_message: ''
              });
              if (loginResponse?.token) {
                Cookies.set('auth_token', loginResponse.token, { expires: 30 });
                await refetchOnboardingData();

                const config = loginResponse.onboarding_config;
                const stage = config?.stage?.toString();
                const step = config?.step;

                if (stage === '2') {
                  navigate(`${urls.businessOnboarding.base}/stage-2`, {
                    replace: true
                  });
                } else {
                  navigate(`${urls.businessOnboarding.base}/stage-1`, {
                    replace: true
                  });
                  if (step) {
                    setFormStep(parseInt(step) - 1);
                  }
                }
              }
            } catch (error) {
              console.error('Login failed:', error);
            }
          }
          return;
        } else if (onboardingId || onboardToken) {
          const response = await updatePublicOnboarding({
            formId: onboardingId || onboardToken || '',
            payload: {
              onboarding_form: buildOnboardingObject(
                values,
                '1',
                formStep + 2,
                true,
                undefined,
                onboardingDataResponse?.onboarding_config
              ),
              update_message: ''
            }
          });
          if (response) {
            const id = response.id.toString();
            Cookies.set('onboarding_id', id, { expires: 30 });
            setOnboardingId(id);

            const config = response.onboarding_config;
            const stage = config?.stage?.toString();
            const step = config?.step;

            if (stage === '1' && (step === '7' || step === '8')) {
              setFormStep(parseInt(step) - 1);
              return;
            }
          }
        }
        setFormStep((prev) => Math.min(prev + 1, 7));
      } catch (error) {
        console.error('Error during step transition:', error);
      }
    }
  };

  const handleBack = () => {
    const minStep = typeof window !== 'undefined' && window.innerWidth < 900 ? -1 : 0;
    setFormStep((prev) => Math.max(prev - 1, minStep));
  };

  const handlePayment = async () => {
    if (!onboardingId) {
      console.error('Missing onboarding data');
      return;
    }

    try {
      // Stripe Checkout Session Creation
      const success_url = `${window.location.origin}${urls.businessOnboarding.base}/${urls.businessOnboarding.stage1()}?success=true`;
      const cancel_url = 'https://kickcall.ai';

      const checkoutResponse = await createCheckout({
        success_url,
        cancel_url,
        quantity: 1,
        referral: Cookies.get(COOKIE_REFERRER_KEY) || '',
        schema_name: window.location.hostname.split('.')[0],
        backend_domain: BACKEND_URL,
        resource_type: 'OnboardingForm',
        resource_id: parseInt(onboardingId),
        customer_id: Cookies.get(COOKIE_CUSTOMER_ID_KEY) || undefined,
        program_id: Cookies.get(COOKIE_PROGRAM_ID_KEY) || undefined,
        partner_id: Cookies.get(COOKIE_PARTNER_ID_KEY) || undefined,
        tolt_referral: Cookies.get(COOKIE_TOLT_REFERRAL_KEY) || undefined
      });

      if (checkoutResponse?.checkout_url) {
        window.location.href = checkoutResponse.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  return (
    <Grid container gap={0} sx={{ height: { xs: '100dvh', md: '100vh' } }}>
      <LeftPanel organizationData={organizationData} />
      <RightPanel
        formStep={formStep}
        control={control}
        setValue={setValue}
        watch={watch}
        handleNext={handleNext}
        handleBack={handleBack}
        handlePayment={handlePayment}
        isSubmittingFinal={isSubmittingFinal}
        isSubmittingPayment={isSubmittingPayment}
        onboardingData={onboardingDataResponse}
        billingEnabled={billingEnabled}
        organizationData={organizationData}
        disabledFields={disabledFields}
      />
    </Grid>
  );
};

export default Onboarding;
